import React, { useEffect, useMemo } from 'react';
import './index.scss';
import { STEPS as STEP_LIST, coverSVG, stepHTML } from '../../common/nexusArt';
import { initConcepts, stageHTML, swapHTML, PINS } from './controller';

const STEPS: any[] = STEP_LIST;

/*
 * The concept walk-through, five sections:
 *   #concepts  a chart that turns into pixels when exported
 *   #whatif    the question, over a lineage river
 *   #native    the LLM-native figure, F = {V, C, D, M}
 *   #bad       Brush · Ask · Discover, stepped one wheel tick at a time
 *   #bidir     bidirectional mapping: code → chart, then chart → code, stepped the same way
 *   #versions  the data-driven artifact
 * The SVGs come from common/nexusArt; controller.ts runs the scroll choreography.
 */
const ConceptsPage: React.FC = () => {
  const html = useMemo(() => ({
    conceptsStage: stageHTML(PINS[0].idx),
    conceptsSwap: swapHTML(PINS[0].idx),
    badStage: stageHTML(PINS[1].idx),
    badSwap: swapHTML(PINS[1].idx),
    river: coverSVG.river(),
    nativeStep: stepHTML(STEPS[2], 2),
    nativeFig: STEPS[2].svg(),
    versionsFig: STEPS[4].svg(1),
    bidirStage: stageHTML(PINS[2].idx),
    bidirSwap: swapHTML(PINS[2].idx),
  }), []);

  useEffect(() => initConcepts(), []);

  return (
    <>
      <section className="rot pin" id="concepts" data-pin="A"><div className="sticky">
        <div className="contain in cols">
          <div className="txt"><div className="swap" dangerouslySetInnerHTML={{ __html: html.conceptsSwap }} /></div>
          <div className="stagewrap" dangerouslySetInnerHTML={{ __html: html.conceptsStage }} />
        </div>
      </div></section>

      <section className="flow words-sec" id="whatif">
        <div className="bgart" id="whatifbg" aria-hidden="true" dangerouslySetInnerHTML={{ __html: html.river }} />
        <div className="words">
          <div className="w">What if a figure could <em>remember</em><br />how it was <em>made</em>?</div>
          <div className="tail">Its data, its code, every step that shaped it.</div>
        </div>
      </section>

      <section className="flow split" id="native"><div className="contain in cols">
        <div className="txt" dangerouslySetInnerHTML={{ __html: html.nativeStep }} />
        <div className="stagewrap"><div className="fig on" data-i="3"><svg role="img" viewBox={STEPS[2].vb} dangerouslySetInnerHTML={{ __html: html.nativeFig }} /></div></div>
      </div></section>

      <section className="rot pin" id="bad" data-pin="B"><div className="sticky">
        <div className="contain in cols">
          <div className="txt"><div className="swap" dangerouslySetInnerHTML={{ __html: html.badSwap }} /></div>
          <div className="stagecol">
            <div className="stagewrap" dangerouslySetInnerHTML={{ __html: html.badStage }} />
            {/* how far the walk-through has got: one segment per wheel step (filled by controller.renderPhase).
                Disabled until the walk-through has been played to the end once; then a click jumps to that step. */}
            <div className="stepprog">{Array.from({ length: STEPS[3].n - 1 }, (_, i) => <button type="button" key={i} disabled aria-label={`Step ${i + 1}`} />)}</div>
          </div>
        </div>
      </div></section>

      <section className="rot pin" id="bidir" data-pin="C"><div className="sticky">
        <div className="contain in cols">
          <div className="txt"><div className="swap" dangerouslySetInnerHTML={{ __html: html.bidirSwap }} /></div>
          <div className="stagewrap" dangerouslySetInnerHTML={{ __html: html.bidirStage }} />
        </div>
      </div></section>

      <section className="flow split" id="versions"><div className="contain in cols">
        <div className="txt"><div className="step on"><h2>Data-Driven <em>Artifact</em></h2><p className="s2">Go back to any step and branch from there.</p><p>Earlier results stay intact while you explore a new direction. Each saved state is a node; each transformation is an edge you can revisit, re-run or share.</p></div></div>
        <div className="stagewrap"><div className="fig on"><svg role="img" aria-label="Step history: Step 3 updated in place, and a new branch extended from Step 2" viewBox="40 4 850 400" dangerouslySetInnerHTML={{ __html: html.versionsFig }} /></div></div>
      </div></section>

      {/* "keep scrolling" hint, bottom-right; controller.wireCue shows it while a wheel-stepped section is on screen */}
      <div className="scrollcue" aria-hidden="true">
        <svg viewBox="0 0 24 24"><path d="M12 5v13M6 12l6 6 6-6" /></svg>
      </div>
    </>
  );
};

export default ConceptsPage;
